import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { PageHeader, Section, Loading } from "../components/Common";
import { Filter, X, TrendingUp, ArrowRight } from "lucide-react";

const STAGES = ["Enquiry", "Application", "Assessment", "Offer", "Accepted", "Waitlisted", "Declined", "Withdrawn"];

function KPI({ label, value, hint }) {
  return (
    <div className="hl-card p-4" data-testid={`adm-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="overline mb-1">{label}</div>
      <div className="font-display font-bold text-3xl text-[color:var(--hl-navy)]">{value}</div>
      {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}

function FunnelBar({ stage, count, max }) {
  const pct = max > 0 ? Math.round((count / max) * 100) : 0;
  return (
    <div className="flex items-center gap-3 py-2" data-testid={`adm-funnel-${stage}`}>
      <div className="w-32 text-sm text-slate-600">{stage}</div>
      <div className="flex-1 bg-slate-100 rounded-full h-6 overflow-hidden relative">
        <div className="bg-gradient-to-r from-[color:var(--hl-navy)] to-[color:var(--hl-orange)] h-full flex items-center justify-end pr-2"
             style={{ width: `${Math.max(6, pct)}%` }}>
          <span className="text-white text-xs font-semibold">{count}</span>
        </div>
      </div>
      <div className="w-12 text-right text-xs text-slate-500 mono">{pct}%</div>
    </div>
  );
}

export default function AdmissionsWorkspace() {
  const [funnel, setFunnel] = useState(null);
  const [prospects, setProspects] = useState([]);
  const [filter, setFilter] = useState({ stage: "", year: "", boarding: "", source: "", q: "" });
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.admissionsFunnel().then(({ data }) => setFunnel(data)).catch(() => {});
    api.admissionsProspects().then(({ data }) => setProspects(data || [])).catch(() => {});
  }, []);

  const stages = funnel?.stages || {};
  const total = funnel?.total || 0;
  const max = useMemo(() => Math.max(...Object.values(stages), 1), [stages]);
  const boardingCount = prospects.filter((p) => p.boarding_preference === "Boarder").length;
  const dayCount = prospects.filter((p) => p.boarding_preference === "Day").length;

  const rows = prospects.filter((p) => {
    if (filter.stage && p.current_stage !== filter.stage) return false;
    if (filter.year && p.proposed_year_group !== filter.year) return false;
    if (filter.boarding && p.boarding_preference !== filter.boarding) return false;
    if (filter.source && p.source !== filter.source) return false;
    if (filter.q) {
      const q = filter.q.toLowerCase();
      if (!`${p.first_name} ${p.last_name} ${p.application_ref}`.toLowerCase().includes(q)) return false;
    }
    return true;
  });
  const years = Array.from(new Set(prospects.map((p) => p.proposed_year_group).filter(Boolean)));
  const sources = Array.from(new Set(prospects.map((p) => p.source).filter(Boolean)));

  if (!funnel) return <Loading />;

  return (
    <div data-testid="admissions-workspace">
      <PageHeader
        kicker="Admissions"
        title="Admissions Command Centre"
        subtitle={`AY 2026 · Third Term intake for 2027. ${total} active prospects — completely distinct from the 1,255 enrolled students.`}
      />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <KPI label="Total prospects" value={total} hint="Live applications in the pipeline" />
        <KPI label="Offers accepted" value={stages.Accepted || 0} hint={`Conversion rate ${funnel.conversion_pct}%`} />
        <KPI label="Boarding demand" value={boardingCount} hint={`Day: ${dayCount}`} />
        <KPI label="Under assessment" value={(stages.Assessment || 0) + (stages.Offer || 0)} hint="In-flight decisions" />
      </div>
      <Section title="Admissions funnel" actions={<TrendingUp className="w-4 h-4 text-slate-400" />}>
        <div className="hl-card p-6">{STAGES.map((s) => (<FunnelBar key={s} stage={s} count={stages[s] || 0} max={max} />))}</div>
      </Section>
      <Section title={`Prospect register (${rows.length} of ${prospects.length})`} actions={<Filter className="w-4 h-4 text-slate-400" />}>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2 mb-3">
          <input className="hl-input" placeholder="Search name / ref" value={filter.q} onChange={(e) => setFilter({ ...filter, q: e.target.value })} data-testid="adm-search" />
          <select className="hl-input" value={filter.stage} onChange={(e) => setFilter({ ...filter, stage: e.target.value })} data-testid="adm-f-stage">
            <option value="">All stages</option>
            {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select className="hl-input" value={filter.year} onChange={(e) => setFilter({ ...filter, year: e.target.value })} data-testid="adm-f-year">
            <option value="">All year groups</option>{years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <select className="hl-input" value={filter.boarding} onChange={(e) => setFilter({ ...filter, boarding: e.target.value })} data-testid="adm-f-boarding">
            <option value="">Boarding + Day</option><option value="Boarder">Boarder</option><option value="Day">Day</option>
          </select>
          <select className="hl-input" value={filter.source} onChange={(e) => setFilter({ ...filter, source: e.target.value })} data-testid="adm-f-source">
            <option value="">All sources</option>{sources.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="hl-card p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr><th className="text-left p-3">Applicant</th><th className="text-left p-3">Reference</th><th className="text-left p-3">Year</th><th className="text-left p-3">Preference</th><th className="text-left p-3">Source</th><th className="text-left p-3">Stage</th><th className="text-left p-3">Received</th><th></th></tr>
            </thead>
            <tbody>
              {rows.slice(0, 60).map((p) => (
                <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`adm-row-${p.id}`}>
                  <td className="p-3">{p.first_name} {p.last_name}</td>
                  <td className="p-3 mono text-xs">{p.application_ref}</td>
                  <td className="p-3 text-xs">{p.proposed_year_group}</td>
                  <td className="p-3 text-xs">{p.boarding_preference}</td>
                  <td className="p-3 text-xs">{p.source}</td>
                  <td className="p-3"><span className="hl-chip text-xs">{p.current_stage}</span></td>
                  <td className="p-3 mono text-xs">{p.application_date}</td>
                  <td className="p-3 text-right"><button className="hl-btn-secondary text-xs" onClick={() => setSelected(p)} data-testid={`adm-open-${p.id}`}>Open</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
      {selected && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-xl max-w-2xl w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="adm-drawer">
            <div className="flex justify-between items-start mb-4">
              <div>
                <div className="overline">Prospect detail</div>
                <div className="font-display font-bold text-2xl text-[color:var(--hl-navy)]">{selected.first_name} {selected.last_name}</div>
                <div className="text-xs text-slate-500 mono">{selected.application_ref}</div>
              </div>
              <button onClick={() => setSelected(null)} className="text-slate-400"><X className="w-5 h-5" /></button>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm mb-4">
              <div className="hl-card p-3"><div className="overline mb-1">Intended year</div><div>{selected.proposed_year_group}</div></div>
              <div className="hl-card p-3"><div className="overline mb-1">Preference</div><div>{selected.boarding_preference}</div></div>
              <div className="hl-card p-3"><div className="overline mb-1">Nationality</div><div>{selected.nationality}</div></div>
              <div className="hl-card p-3"><div className="overline mb-1">Source</div><div>{selected.source}</div></div>
              <div className="hl-card p-3"><div className="overline mb-1">Gender</div><div>{selected.gender === "M" ? "Male" : "Female"}</div></div>
              <div className="hl-card p-3"><div className="overline mb-1">Received</div><div>{selected.application_date}</div></div>
              <div className="hl-card p-3"><div className="overline mb-1">Documents</div><div className="capitalize">{selected.documents_status}</div></div>
              <div className="hl-card p-3"><div className="overline mb-1">Assessment</div><div className="capitalize">{selected.assessment_status}</div></div>
              <div className="hl-card p-3"><div className="overline mb-1">Decision</div><div>{selected.decision}</div></div>
              <div className="hl-card p-3"><div className="overline mb-1">Offer status</div><div>{selected.offer_status || "—"}</div></div>
              <div className="hl-card p-3 col-span-2"><div className="overline mb-1">Intended intake</div><div>{selected.intended_entry_term} {selected.intended_entry_year}</div></div>
            </div>
            <div className="flex items-center gap-2 py-3 border-t border-slate-100">
              <div className="hl-chip hl-chip-dark px-3 py-1.5 rounded-full text-xs font-semibold">Current stage: {selected.current_stage}</div>
              <ArrowRight className="w-4 h-4 text-slate-400" />
              <div className="text-xs text-slate-500">Stage transitions are controlled &amp; audited via `admissions_history`.</div>
            </div>
            <div className="text-xs text-slate-400 mt-4">Prospect ID <span className="mono">{selected.id}</span> · not counted against the 1,255 enrolled population.</div>
          </div>
        </div>
      )}
    </div>
  );
}
